import React from "react";
import { FormControl, Select, MenuItem, Typography, Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import useModules from "../../hooks/useModules";

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";

export default function ModuleSwitcher({
  current,
  primaryAccess,
  secondaryAccess,
  primaryValue,
  secondaryValue,
  primaryLabel,
  secondaryLabel,
  primaryRoute,
  secondaryRoute,
  singleLabel,
}) {
  const navigate = useNavigate();
  const { hasComponent } = useModules();

  const canSeePrimary = hasComponent(primaryAccess);
  const canSeeSecondary = hasComponent(secondaryAccess);
  const canSwitch = canSeePrimary && canSeeSecondary;

  if (!canSwitch) {
    return (
      <Typography variant="h6" fontWeight={700} sx={{ color: BRAND_BLUE, px: 1 }}>
        {singleLabel({ canSeePrimary, canSeeSecondary })}
      </Typography>
    );
  }

  return (
    <FormControl size="small">
      <Select
        value={current}
        onChange={(e) => {
          navigate(e.target.value === primaryValue ? primaryRoute : secondaryRoute);
        }}
        variant="outlined"
        IconComponent={SwapHorizIcon}
        renderValue={(val) => {
          const label = val === primaryValue ? primaryLabel : secondaryLabel;
          return (
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1.25rem",
                color: BRAND_BLUE,
                lineHeight: 1,
              }}
            >
              {label}
            </Typography>
          );
        }}
        sx={{
          backgroundColor: "rgba(0,51,108,0.06)",
          border: "1.5px solid rgba(0,51,108,0.18)",
          borderRadius: "10px",
          transition: "all 0.15s ease",
          "& fieldset": { border: "none" },
          "& .MuiSelect-select": {
            display: "flex",
            alignItems: "center",
            py: "6px",
            pl: "14px",
            pr: "44px !important",
          },
          "& .MuiSelect-icon": {
            color: BRAND_GREEN,
            fontSize: 20,
            right: 10,
            opacity: 0.7,
          },
          "&:hover": {
            backgroundColor: "rgba(0,51,108,0.1)",
            borderColor: "rgba(0,51,108,0.35)",
          },
          "&.Mui-focused": {
            backgroundColor: "rgba(0,51,108,0.08)",
            borderColor: BRAND_GREEN,
          },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              mt: 0.5,
              borderRadius: 2,
              border: "1px solid rgba(0,51,108,0.15)",
              boxShadow: "0 4px 20px rgba(0,51,108,0.12)",
              minWidth: 200,
            },
          },
        }}
      >
        {canSeePrimary && (
          <MenuItem
            value={primaryValue}
            sx={{
              py: 1.2,
              px: 2,
              "&.Mui-selected": {
                backgroundColor: "rgba(0,51,108,0.08)",
                "&:hover": { backgroundColor: "rgba(0,51,108,0.12)" },
              },
              "&:hover": { backgroundColor: "rgba(0,51,108,0.06)" },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              {current === primaryValue && (
                <Box sx={{ width: 3, height: 16, borderRadius: 2, bgcolor: BRAND_GREEN, flexShrink: 0 }} />
              )}
              <Typography
                sx={{
                  fontWeight: current === primaryValue ? 700 : 500,
                  fontSize: "0.95rem",
                  color: BRAND_BLUE,
                }}
              >
                {primaryLabel}
              </Typography>
            </Box>
          </MenuItem>
        )}

        {canSeeSecondary && (
          <MenuItem
            value={secondaryValue}
            sx={{
              py: 1.2,
              px: 2,
              "&.Mui-selected": {
                backgroundColor: "rgba(0,51,108,0.08)",
                "&:hover": { backgroundColor: "rgba(0,51,108,0.12)" },
              },
              "&:hover": { backgroundColor: "rgba(0,51,108,0.06)" },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              {current === secondaryValue && (
                <Box sx={{ width: 3, height: 16, borderRadius: 2, bgcolor: BRAND_GREEN, flexShrink: 0 }} />
              )}
              <Typography
                sx={{
                  fontWeight: current === secondaryValue ? 700 : 500,
                  fontSize: "0.95rem",
                  color: BRAND_BLUE,
                }}
              >
                {secondaryLabel}
              </Typography>
            </Box>
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );
}
